// src/shared/bug-reporter/BugReporter.tsx
// Canonical web bug reporter. Mounted from a host app's root layout. Self-gates
// visibility via NODE_ENV and the ENABLE_BUG_REPORTER flag — host apps do not
// need to wrap this in a condition.

import { useCallback, useState } from 'react'
import { ReporterButton } from './components/ReporterButton'
import { ReporterModal } from './components/ReporterModal'
import { useKeyboardShortcut } from './hooks/useKeyboardShortcut'
import { isReporterVisible } from './visibility'
import type { BugReporterProps } from './types'

export function BugReporter({
  appName,
  apiBaseUrl,
  user,
  enabled,
  recentActions,
  getAuthToken,
}: BugReporterProps) {
  // Read Vite env at module level — this is fine because Vite inlines the values
  // and the gate is evaluated at render time, not at import time.
  const mode = import.meta.env.MODE
  const flagValue = import.meta.env.VITE_ENABLE_BUG_REPORTER

  const visible = isReporterVisible({
    enabledOverride: enabled,
    mode,
    flagValue,
  })

  const [open, setOpen] = useState(false)
  const trigger = useCallback(() => setOpen(true), [])

  useKeyboardShortcut({ enabled: visible, onTrigger: trigger })

  if (!visible) return null

  return (
    <>
      <ReporterButton onClick={trigger} />
      <ReporterModal
        open={open}
        onOpenChange={setOpen}
        appName={appName}
        apiBaseUrl={apiBaseUrl}
        user={user}
        recentActions={recentActions}
        getAuthToken={getAuthToken}
      />
    </>
  )
}
